import { Link } from 'react-router-dom';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardHeader } from '../../../components/ui/Card';
import type { ReplyRow } from '../../../api/outreach';
import { formatDateTime } from './format';

function VerdictBadge({ verdict }: { verdict: ReplyRow['verdict'] }) {
  if (verdict === 'interested') return <Badge variant="success">Interested</Badge>;
  if (verdict === 'not_interested') return <Badge variant="error">Not interested</Badge>;
  return <Badge>No verdict</Badge>;
}

function stepLabel(reply: ReplyRow): string {
  if (reply.stepIndex !== null) return `Step ${reply.stepIndex}`;
  // A step id that no longer exists in the synced sequence: the step was edited after the send.
  if (reply.stepId) return 'Removed step';
  return 'Step unknown';
}

/**
 * The replies themselves, newest first. At these volumes reading five replies
 * says more than a percentage does.
 */
export function RepliesList({ replies }: { replies: ReplyRow[] }) {
  return (
    <Card padding="none">
      <div className="p-4 pb-0">
        <CardHeader
          title="Replies"
          description={
            replies.length >= 50
              ? 'The newest 50 replies in this period.'
              : 'Every reply in this period, newest first. The verdict comes from a later interested / not-interested mark in lemlist.'
          }
        />
      </div>
      <div className="p-4 pt-0">
        {replies.length === 0 ? (
          <p className="py-6 text-center text-body-sm text-text-tertiary">No replies in this period.</p>
        ) : (
          <ul className="divide-y divide-border-light">
            {replies.map((reply) => (
              <li key={reply.activityId} className="py-3 space-y-1.5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm">
                  <span className="font-medium text-text-primary">{reply.leadName ?? 'Unnamed lead'}</span>
                  {reply.leadCompanyName && <span className="text-text-secondary">{reply.leadCompanyName}</span>}
                  <VerdictBadge verdict={reply.verdict} />
                  {reply.interestScore !== null && (
                    <span
                      className="text-body-xs text-text-tertiary tabular-nums cursor-help"
                      title="lemlist's AI lead-interest score"
                    >
                      interest {reply.interestScore}
                    </span>
                  )}
                  <span className="text-body-xs text-text-tertiary">{stepLabel(reply)}</span>
                  <span className="ml-auto flex items-center gap-3 text-body-xs text-text-tertiary">
                    {reply.leadLinkedinUrl && (
                      <a
                        href={reply.leadLinkedinUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent hover:underline"
                      >
                        LinkedIn
                      </a>
                    )}
                    {reply.candidateId && (
                      <Link to="/gtm/plays" className="text-accent hover:underline" title={`Candidate ${reply.candidateId}`}>
                        In GTM → Plays
                      </Link>
                    )}
                    <span className="whitespace-nowrap">{formatDateTime(reply.occurredAt)}</span>
                  </span>
                </div>
                {reply.subject && <p className="text-body-xs font-medium text-text-secondary">{reply.subject}</p>}
                {reply.text ? (
                  <p className="whitespace-pre-wrap text-body-sm text-text-secondary line-clamp-4" title={reply.text}>
                    {reply.text}
                  </p>
                ) : (
                  <p className="text-body-xs text-text-tertiary">No reply text stored.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
